import { useState, useEffect, useContext, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import { ModalContext } from "contexts/Modal";

/** Allowed: letters, numbers, spaces, underscore, hyphen; length 1–255 */
const BRAND_NAME_PATTERN = "[A-Za-z0-9 _\\-]{1,255}"; // for <input pattern>
const BRAND_NAME_RE = /^[A-Za-z0-9 _\-]{1,255}$/;     // for JS validation
const BRAND_NAME_TITLE =
  "Only letters, numbers, spaces, underscore (_), and hyphen (-). Max 255 characters.";

const emptyInputs = { brandName: "" };

const useUserBrands = () => {
  const [userBrands, setBrands] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [inputs, setInputs] = useState(emptyInputs);
  const { showModal } = useContext(ModalContext);

  // ---- Auth gate ----
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setBrands([]);          // tidy up on sign out
          setInputs(emptyInputs);
          setModalOpen(false);
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  const ensureAuthed = async () => {
    await Auth.currentAuthenticatedUser();
  };

  // ---- Load brands when authed ----
  const inFlight = useRef(false);
  const fetchUserBrands = async () => {
    if (!ready || inFlight.current) return;
    inFlight.current = true;

    try {
      await ensureAuthed();

      const brandsData = await API.get("AccountGatewayApi", "/brands");

      const brandAccounts = await Promise.all(
        (brandsData || []).map((brand) =>
          API.get("AccountGatewayApi", `/brand/${brand.name}`).catch(() => ({}))
        )
      );

      setBrands(
        (brandsData || []).map((brand, idx) => ({
          brandName: brand.name,
          accountName: (brandAccounts[idx]?.accounts || [])
            .map((account) => account.name)
            .join(", "),
        }))
      );
    } catch (err) {
      console.error("fetchUserBrands error", err);
    } finally {
      inFlight.current = false;
    }
  };

  useEffect(() => {
    if (!ready) return;
    fetchUserBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const handleSave = async () => {
    try {
      await ensureAuthed();

      const name = (inputs.brandName || "").trim();
      if (!BRAND_NAME_RE.test(name)) {
        showModal(
          "Brand name is invalid",
          "Use only letters, numbers, spaces, underscore (_), and hyphen (-), up to 255 characters."
        );
        return;
      }

      await API.post("AccountGatewayApi", `/brand`, {
        body: { name },
      });

      setModalOpen(false);
      showModal("Brand Save", "Save was successful.");
      setInputs(emptyInputs);
      fetchUserBrands(); // refresh list after save
    } catch (err) {
      console.error("Error saving new brand", err);
      showModal("Brand save unsuccessful", "Error occurred while saving new brand.");
    }
  };

  return {
    userBrands,
    modalOpen,
    setModalOpen,
    inputs,
    setInputs,
    handleSave,
    // expose pattern/title for your input field
    brandNamePattern: BRAND_NAME_PATTERN,
    brandNameTitle: BRAND_NAME_TITLE,
  };
};

export default useUserBrands;
